/* Admin view — platform health + audit log + waitlist signups.
   Only accessible if /api/admin/me returns is_admin=true. */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import UsersPanel from "@/components/admin/UsersPanel";
import JourneysPanel from "@/components/admin/JourneysPanel";
import ValuationsPanel from "@/components/admin/ValuationsPanel";
import NudgesPanel from "@/components/admin/NudgesPanel";
import FmModelsPanel from "@/components/admin/FmModelsPanel";
import { toastApiError } from "@/lib/apiError";
import {
  ArrowLeft, ShieldCheck, CheckCircle, WarningCircle, XCircle,
  Database, Cloud, Users, FileText, ChartLineUp, EnvelopeSimple, ListChecks,
  PaperPlaneTilt, Copy, ChatCircleText, Sparkle,
} from "@phosphor-icons/react";

const C = { bgDark: "#000", panel: "#0A2826", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", red: "#F87171" };

const ClinqMark = () => (
  <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
    style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
    Cl<span className="relative inline-block">ı<span aria-hidden className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
      style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span></span>nq
  </span>
);

export default function Admin() {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null);
  const [health, setHealth] = useState(null);
  const [audit, setAudit] = useState({ rows: [], count: 0 });
  const [waitlist, setWaitlist] = useState({ rows: [], count: 0 });
  const [emailStatus, setEmailStatus] = useState(null);
  const [topQuestions, setTopQuestions] = useState(null);
  const [topDays, setTopDays] = useState(7);
  const [tab, setTab] = useState("users");
  const [filter, setFilter] = useState({ level: "", event: "", since_hours: 24 });

  // Gate access
  useEffect(() => {
    api.get("/admin/me").then(({ data }) => {
      setAllowed(!!data.is_admin);
      if (!data.is_admin) {
        toast.error("Admin access required");
        setTimeout(() => navigate("/dashboard"), 1200);
      }
    }).catch(() => { setAllowed(false); navigate("/login"); });
  }, [navigate]);

  // Initial load
  useEffect(() => {
    if (allowed !== true) return;
    api.get("/admin/health").then(({ data }) => setHealth(data)).catch(() => {});
    api.get("/admin/email-status").then(({ data }) => setEmailStatus(data)).catch(() => {});
    refreshAudit();
    refreshTopQuestions(topDays);
    api.get("/admin/waitlist?limit=200").then(({ data }) => setWaitlist(data)).catch(() => {});
  }, [allowed]);

  const refreshAudit = () => {
    const params = new URLSearchParams();
    if (filter.level) params.set("level", filter.level);
    if (filter.event) params.set("event", filter.event);
    if (filter.since_hours) params.set("since_hours", filter.since_hours);
    params.set("limit", "200");
    api.get(`/admin/audit?${params}`).then(({ data }) => setAudit(data)).catch(() => {});
  };

  const refreshTopQuestions = (days) => {
    api.get(`/admin/top-questions?days=${days}&limit=15`)
      .then(({ data }) => setTopQuestions(data))
      .catch(() => setTopQuestions({ rows: [], total_chats: 0, unique_users: 0, days }));
  };

  if (allowed === null) {
    return <div className="min-h-screen grid place-items-center text-white/45" style={{ backgroundColor: C.bgDark }}>Checking access…</div>;
  }
  if (allowed === false) {
    return <div className="min-h-screen grid place-items-center text-white/45" style={{ backgroundColor: C.bgDark }} data-testid="admin-denied">Forbidden — redirecting…</div>;
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="admin-page">
      <header style={{ backgroundColor: "#000" }} className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-white/55 hover:text-white" data-testid="admin-back">
              <ArrowLeft size={16} />
            </Link>
            <Link to="/"><ClinqMark /></Link>
            <div className="hidden sm:flex flex-col leading-none border-l pl-3 border-white/15">
              <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: C.orange }}>Platform</span>
              <span className="text-[9px] uppercase tracking-[0.22em] font-semibold mt-0.5 text-white/55">Admin console</span>
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-white/55">
            <ShieldCheck size={14} weight="bold" style={{ color: C.tealSoft }} /> Admin
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-8 space-y-8">
        {/* Health */}
        <section data-testid="admin-health">
          <h2 className="text-2xl font-extrabold tracking-tight mb-4" style={{ letterSpacing: "-0.02em" }}>
            Platform <span style={{ color: C.teal }}>health</span>
          </h2>
          {!health ? <div className="text-white/45 text-sm">Loading…</div> : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatusTile icon={<Cloud size={16} />} label="Object storage" status={health.storage} ok={health.storage === "ok"} />
              <StatusTile icon={<Database size={16} />} label="Database" status={health.database} ok={health.database === "ok"} />
              <StatusTile icon={<ListChecks size={16} />} label="Audit (24h)"
                status={`${health.audit?.last_24h?.total || 0} events`}
                ok={(health.audit?.last_24h?.by_level?.error || 0) === 0}
                sub={`${health.audit?.last_24h?.by_level?.warn || 0} warn · ${health.audit?.last_24h?.by_level?.error || 0} error`} />
              <StatusTile icon={<EnvelopeSimple size={16} />} label="Waitlist signups" status={`${health.counts?.waitlist || 0}`} ok={true} />
            </div>
          )}
          {health && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
              <CountTile icon={<Users size={12} />} label="Users" v={health.counts?.users} />
              <CountTile icon={<ChartLineUp size={12} />} label="Models" v={health.counts?.models} />
              <CountTile icon={<FileText size={12} />} label="Pitch decks" v={health.counts?.pitch_decks} />
              <CountTile icon={<FileText size={12} />} label="Public decks" v={health.counts?.pitch_decks_public} />
              <CountTile icon={<ChartLineUp size={12} />} label="Mgmt accounts" v={health.counts?.mgmt_accounts} />
              <CountTile icon={<ListChecks size={12} />} label="Audit rows" v={health.counts?.audit_rows} />
            </div>
          )}
        </section>

        {/* Email delivery */}
        <EmailDelivery status={emailStatus} onRefresh={() => api.get("/admin/email-status").then(({ data }) => setEmailStatus(data))} />

        {/* Top questions this week */}
        <TopQuestions data={topQuestions} days={topDays}
          onDaysChange={(d) => { setTopDays(d); refreshTopQuestions(d); }}
          onRefresh={() => refreshTopQuestions(topDays)} />

        {/* One-off migrations */}
        <Migrations />

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-2">
          {[["users", "Users & activity"], ["journeys", "Journeys"], ["valuations", "Valuations"], ["models", "Fin models"], ["david", "David chats"], ["nudges", "Nudges"], ["crashes", "Crashes"], ["audit", "Audit log"], ["waitlist", "Waitlist signups"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className="text-xs font-semibold uppercase tracking-[0.18em] px-3 py-2 rounded-md transition-colors"
              style={{
                backgroundColor: tab === id ? C.teal : "transparent",
                color: tab === id ? "#fff" : "rgba(255,255,255,0.55)",
              }}>{label}</button>
          ))}
        </div>

        {tab === "users" && <UsersPanel />}

        {tab === "journeys" && <JourneysPanel />}

        {tab === "valuations" && <ValuationsPanel />}

        {tab === "models" && <FmModelsPanel />}

        {tab === "david" && <DavidChatsPanel />}

        {tab === "nudges" && <NudgesPanel />}

        {tab === "crashes" && <CrashesPanel />}

        {tab === "audit" && (
          <section data-testid="admin-audit-section">
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <Filter label="Level" value={filter.level} onChange={(v) => setFilter({ ...filter, level: v })}
                options={[["", "All"], ["info", "Info"], ["warn", "Warn"], ["error", "Error"]]} testid="admin-filter-level" />
              <Filter label="Event" value={filter.event} onChange={(v) => setFilter({ ...filter, event: v })}
                options={[["", "All"], ["waitlist.join", "waitlist.join"], ["storage.fallback", "storage.fallback"]]} testid="admin-filter-event" />
              <Filter label="Since" value={String(filter.since_hours)} onChange={(v) => setFilter({ ...filter, since_hours: Number(v) })}
                options={[["1", "1 hour"], ["24", "24 hours"], ["168", "7 days"], ["720", "30 days"]]} testid="admin-filter-since" />
              <button onClick={refreshAudit} data-testid="admin-refresh-audit"
                className="px-3 py-2 rounded-md text-xs font-semibold" style={{ backgroundColor: C.teal, color: "#fff" }}>Apply</button>
            </div>

            <div className="rounded-xl border overflow-x-auto" style={{ borderColor: "rgba(123, 211, 206, 0.12)" }}>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-white/45 uppercase tracking-wider text-[10px]" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Level</th>
                    <th className="px-3 py-2">Event</th>
                    <th className="px-3 py-2">Entity</th>
                    <th className="px-3 py-2">Message</th>
                    <th className="px-3 py-2">User</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.rows.length === 0 && (
                    <tr><td colSpan="6" className="px-3 py-8 text-center text-white/30">No audit rows match this filter.</td></tr>
                  )}
                  {audit.rows.map((r) => (
                    <tr key={r.id} className="border-t border-white/5" data-testid={`audit-row-${r.id}`}>
                      <td className="px-3 py-2 font-mono-num text-white/55 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                          style={{
                            backgroundColor: r.level === "error" ? "rgba(248,113,113,0.15)" : (r.level === "warn" ? "rgba(245,158,11,0.15)" : "rgba(10,139,133,0.18)"),
                            color: r.level === "error" ? C.red : (r.level === "warn" ? C.orange : C.tealSoft),
                          }}>{r.level}</span>
                      </td>
                      <td className="px-3 py-2 font-mono-num">{r.event}</td>
                      <td className="px-3 py-2 text-white/45 text-[11px]">
                        {r.entity_type ? `${r.entity_type}/${(r.entity_id || "").slice(0, 14)}…` : "—"}
                      </td>
                      <td className="px-3 py-2 truncate max-w-md" title={r.message}>{r.message || "—"}</td>
                      <td className="px-3 py-2 text-white/40 text-[11px] font-mono-num">{(r.user_id || "—").slice(0, 16)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "waitlist" && (
          <section data-testid="admin-waitlist-section">
            <div className="rounded-xl border overflow-x-auto" style={{ borderColor: "rgba(123, 211, 206, 0.12)" }}>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-white/45 uppercase tracking-wider text-[10px]" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Service</th>
                    <th className="px-3 py-2">Company</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Email sent</th>
                  </tr>
                </thead>
                <tbody>
                  {waitlist.rows.length === 0 && (
                    <tr><td colSpan="6" className="px-3 py-8 text-center text-white/30">No signups yet.</td></tr>
                  )}
                  {waitlist.rows.map((r) => (
                    <tr key={r.id} className="border-t border-white/5">
                      <td className="px-3 py-2 font-mono-num text-white/55 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2">{r.email}</td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                          style={{ backgroundColor: "rgba(10,139,133,0.18)", color: C.tealSoft }}>{r.service}</span>
                      </td>
                      <td className="px-3 py-2 text-white/55">{r.company || "—"}</td>
                      <td className="px-3 py-2 text-white/40 text-[11px]">{r.source || "—"}</td>
                      <td className="px-3 py-2 text-[11px] font-mono-num text-white/40">
                        {r.email_sent_id ? <CheckCircle size={12} weight="fill" style={{ color: C.teal }} /> : <XCircle size={12} style={{ color: C.red }} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

const KIND_BADGE = {
  pitch_deck: { label: "Pitch", color: "#7BD3CE" },
  financial_model: { label: "Model", color: "#F59E0B" },
  mgmt_accounts: { label: "Mgmt", color: "#9AE6B4" },
};

function Migrations() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(null);

  const dryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/migrate/pitch-stage?dry_run=true");
      setPreview(data);
      setApplied(null);
    } catch (e) {
      toastApiError(e, "Migration preview failed", { context: { action: "pitch_stage_migration_dryrun" } });
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview || preview.would_change === 0) return;
    if (!window.confirm(`Apply migration to ${preview.would_change} pitch decks? This rewrites the 'stage' field. Already-migrated decks are skipped.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/migrate/pitch-stage?dry_run=false");
      setApplied(data);
      setPreview(data);
      toast.success(`Migrated ${data.applied} decks`);
    } catch (e) {
      toastApiError(e, "Migration failed", { context: { action: "pitch_stage_migration_apply" } });
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="admin-migrations">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-bold mb-1.5" style={{ color: C.orange }}>
            <ListChecks size={12} weight="fill" /> One-off migrations
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
            Normalize legacy <span style={{ color: C.teal }}>pitch deck stages</span>
          </h2>
          <p className="text-white/50 text-sm mt-1 max-w-2xl">
            Maps old free-form values (Seed, Series A, Growth, etc.) to the new Pre-revenue / Post-revenue toggle.
            Already-migrated decks are skipped. Always run a dry-run first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={dryRun} disabled={busy} data-testid="admin-migrate-preview"
            className="px-3 py-2 rounded-md text-[11px] font-bold uppercase tracking-[0.15em]"
            style={{ border: `1px solid ${C.tealSoft}`, color: C.tealSoft, opacity: busy ? 0.5 : 1 }}>
            {busy ? "Working…" : "Preview"}
          </button>
          <button onClick={apply}
            disabled={busy || !preview || preview.would_change === 0}
            data-testid="admin-migrate-apply"
            className="px-3 py-2 rounded-md text-[11px] font-bold uppercase tracking-[0.15em]"
            style={{
              backgroundColor: C.orange, color: "#000",
              opacity: (busy || !preview || preview.would_change === 0) ? 0.4 : 1,
            }}>
            Apply migration
          </button>
        </div>
      </div>

      {!preview ? (
        <div className="text-white/40 text-xs italic">No preview yet — click Preview to see what would change.</div>
      ) : (
        <div className="rounded-xl border p-4 grid sm:grid-cols-2 gap-4" style={{ borderColor: "rgba(123, 211, 206, 0.12)", backgroundColor: "rgba(0,0,0,0.25)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/45 mb-2">Summary</div>
            <ul className="space-y-1 text-[12px]">
              <li className="flex justify-between"><span className="text-white/60">Total decks</span><span className="font-mono-num font-bold text-white">{preview.total_decks}</span></li>
              <li className="flex justify-between"><span className="text-white/60">Already migrated</span><span className="font-mono-num text-white/70">{preview.already_migrated}</span></li>
              <li className="flex justify-between"><span className="text-white/60">{applied ? "Applied" : "Would change"}</span><span className="font-mono-num font-bold" style={{ color: C.orange }}>{applied ? preview.applied : preview.would_change}</span></li>
              <li className="flex justify-between"><span className="text-white/60">→ Pre-revenue</span><span className="font-mono-num" style={{ color: C.tealSoft }}>{preview.to_counts?.["Pre-revenue"] || 0}</span></li>
              <li className="flex justify-between"><span className="text-white/60">→ Post-revenue</span><span className="font-mono-num" style={{ color: C.green }}>{preview.to_counts?.["Post-revenue"] || 0}</span></li>
            </ul>
            {applied && (
              <div className="mt-3 text-[11px] px-2.5 py-1.5 rounded inline-block font-bold" style={{ backgroundColor: `${C.green}22`, color: C.green }}>
                ✓ Migration applied · {applied.applied} decks updated
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/45 mb-2">From → To (sample)</div>
            {Object.entries(preview.from_counts || {}).length === 0 ? (
              <div className="text-white/40 text-[12px] italic">Nothing to migrate.</div>
            ) : (
              <ul className="space-y-1 text-[12px]">
                {Object.entries(preview.from_counts || {})
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 12)
                  .map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between py-1 border-b border-white/5">
                      <span className="text-white/80 font-mono-num">{k}</span>
                      <span className="text-white/40 font-mono-num text-[11px]">{v} deck{v > 1 ? "s" : ""}</span>
                    </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}


function TopQuestions({ data, days, onDaysChange, onRefresh }) {
  return (
    <section data-testid="admin-top-questions">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-bold mb-1.5" style={{ color: C.orange }}>
            <Sparkle size={12} weight="fill" /> Clinq AI · founder signal
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
            Top questions <span style={{ color: C.teal }}>this {days === 1 ? "day" : `${days} days`}</span>
          </h2>
          <p className="text-white/50 text-sm mt-1">Where founders are getting stuck across pitch decks, financial models and management accounts.</p>
        </div>
        <div className="flex items-center gap-2">
          {[1, 7, 30].map((d) => (
            <button key={d} onClick={() => onDaysChange(d)}
              data-testid={`admin-topq-days-${d}`}
              className="px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.15em] transition-colors"
              style={{
                backgroundColor: days === d ? C.teal : "rgba(0,0,0,0.35)",
                color: days === d ? "#fff" : "rgba(255,255,255,0.55)",
                border: `1px solid ${days === d ? C.teal : "rgba(255,255,255,0.12)"}`,
              }}>{d === 1 ? "24h" : `${d}d`}</button>
          ))}
          <button onClick={onRefresh} data-testid="admin-topq-refresh"
            className="px-3 py-1.5 rounded-md text-[11px] font-semibold"
            style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.65)" }}>Refresh</button>
        </div>
      </div>

      {!data ? (
        <div className="text-white/45 text-sm">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <CountTile icon={<ChatCircleText size={12} weight="fill" />} label="Total conversations" v={data.total_chats} />
            <CountTile icon={<Users size={12} weight="fill" />} label="Unique founders" v={data.unique_users} />
            <CountTile icon={<ListChecks size={12} weight="fill" />} label="Distinct questions" v={data.rows?.length || 0} />
            <CountTile icon={<Sparkle size={12} weight="fill" />} label="Window (days)" v={data.days} />
          </div>

          {(!data.rows || data.rows.length === 0) ? (
            <div className="rounded-xl border px-6 py-10 text-center text-white/40 text-sm" style={{ borderColor: "rgba(123, 211, 206, 0.12)", backgroundColor: "rgba(0,0,0,0.2)" }} data-testid="admin-topq-empty">
              No Clinq AI conversations in this window yet. Once founders start asking, this panel will surface the patterns.
            </div>
          ) : (
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: "rgba(123, 211, 206, 0.12)" }}>
              {data.rows.map((r, i) => {
                const max = data.rows[0]?.count || 1;
                const pct = Math.round((r.count / max) * 100);
                return (
                  <div key={`${r.question}-${i}`} className="px-4 py-3 border-t border-white/5 first:border-t-0"
                    data-testid={`admin-topq-row-${i}`}
                    style={{ backgroundColor: i === 0 ? "rgba(10,139,133,0.06)" : "transparent" }}>
                    <div className="flex items-start gap-3">
                      <div className="text-white/35 font-mono-num text-xs pt-0.5 w-5 text-right">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white/90 leading-snug">{r.question}</div>
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                          {(r.kinds || []).filter(Boolean).map((k) => {
                            const b = KIND_BADGE[k] || { label: k, color: "#9CA3AF" };
                            return (
                              <span key={k} className="text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded"
                                style={{ backgroundColor: `${b.color}22`, color: b.color }}>{b.label}</span>
                            );
                          })}
                          <span className="text-[11px] text-white/40">
                            {r.unique_users} {r.unique_users === 1 ? "founder" : "founders"}
                            {r.last_seen ? ` · last seen ${new Date(r.last_seen).toLocaleDateString()}` : ""}
                          </span>
                        </div>
                        <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: i === 0 ? C.orange : C.teal }} />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-extrabold font-mono-num" style={{ color: i === 0 ? C.orange : C.tealSoft }}>{r.count}</div>
                        <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-white/30">asks</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}


function StatusTile({ icon, label, status, ok, sub }) {
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: ok ? "rgba(10,139,133,0.35)" : "rgba(248,113,113,0.35)", backgroundColor: C.panel }}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/45 inline-flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="text-xl font-extrabold mt-1.5 inline-flex items-center gap-2"
        style={{ color: ok ? C.teal : C.red }}>
        {ok ? <CheckCircle size={16} weight="fill" /> : <WarningCircle size={16} weight="fill" />} {status}
      </div>
      {sub && <div className="text-[11px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

function CountTile({ icon, label, v }) {
  return (
    <div className="rounded-md p-3 border" style={{ borderColor: "rgba(255,255,255,0.08)", backgroundColor: "rgba(0,0,0,0.25)" }}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/40 inline-flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-lg font-extrabold mt-0.5" style={{ color: C.tealSoft }}>{v ?? "—"}</div>
    </div>
  );
}

function Filter({ label, value, onChange, options, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/40 mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="px-3 py-2 rounded-md text-xs"
        style={{ backgroundColor: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff" }}>
        {options.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
      </select>
    </div>
  );
}

function EmailDelivery({ status, onRefresh }) {
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [reverifying, setReverifying] = useState(false);
  const [lastSend, setLastSend] = useState(null);

  const send = async () => {
    if (!to) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/test-email", { to });
      setLastSend({ to: data.to, id: data.resend_id, from: data.from });
      toast.success(`Sent. Resend id ${data.resend_id?.slice(0, 8)}…`);
    } catch (e) {
      toastApiError(e, "Send failed", { context: { action: "admin_test_email", to } });
    } finally { setSending(false); }
  };

  const reverify = async () => {
    setReverifying(true);
    try {
      await api.post("/admin/email-reverify");
      toast.success("Resend will re-check DNS. Click Refresh in ~30 seconds.");
      setTimeout(onRefresh, 4000);
    } catch (e) {
      toastApiError(e, "Re-verify failed", { context: { action: "admin_email_reverify" } });
    } finally { setReverifying(false); }
  };

  const statusLabel = status?.status || "unknown";
  const isVerified = statusLabel === "verified";
  const colour = isVerified ? "#0A8B85" : (statusLabel === "failed" ? "#F87171" : "#F59E0B");

  return (
    <section data-testid="admin-email-delivery">
      <h2 className="text-2xl font-extrabold tracking-tight mb-4" style={{ letterSpacing: "-0.02em" }}>
        Email <span style={{ color: C.teal }}>delivery</span>
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Domain status */}
        <div className="rounded-xl border p-5" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/45">Resend domain status</div>
              <div className="text-2xl font-extrabold mt-1 inline-flex items-center gap-2" style={{ color: colour }} data-testid="email-domain-status">
                {isVerified ? <CheckCircle size={18} weight="fill" /> : <WarningCircle size={18} weight="fill" />}
                <span className="uppercase">{statusLabel}</span>
              </div>
              <div className="text-sm text-white/55 mt-2">
                Sending as <span className="font-mono-num text-white/85">{status?.sender_email || "—"}</span>
              </div>
              {!isVerified && (
                <p className="text-xs text-white/55 mt-3 max-w-md leading-relaxed">
                  Until status is <strong style={{ color: C.teal }}>verified</strong>, emails may not reach Gmail/Outlook inboxes.
                  Add the DNS records below at your registrar, then click Refresh.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5 items-end shrink-0">
              <button onClick={onRefresh} data-testid="refresh-email-status"
                className="text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-white/10"
                style={{ border: "1px solid rgba(255,255,255,0.15)" }}>
                Refresh
              </button>
              {!isVerified && (
                <button onClick={reverify} disabled={reverifying} data-testid="reverify-domain"
                  className="text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50"
                  style={{ backgroundColor: C.teal, color: "#fff" }}>
                  {reverifying ? "…" : "Re-verify"}
                </button>
              )}
            </div>
          </div>

          {!isVerified && Array.isArray(status?.records) && status.records.length > 0 && (
            <details className="mt-4" data-testid="dns-records-details">
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: C.tealSoft }}>
                Show DNS records to add ({status.records.length})
              </summary>
              <div className="mt-3 space-y-2">
                {status.records.map((r, i) => (
                  <div key={i} className="rounded-md p-3 text-[11px]" style={{ backgroundColor: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <div className="font-mono-num font-bold mb-1" style={{ color: C.tealSoft }}>{r.type} record</div>
                    <DNSField label="Name" value={r.name} />
                    <DNSField label="Value" value={r.value} long />
                    {r.priority !== undefined && <DNSField label="Priority" value={String(r.priority)} />}
                    <DNSField label="TTL" value={r.ttl || "Auto"} />
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Send test */}
        <div className="rounded-xl border p-5" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/45">Send a delivery test</div>
          <p className="text-xs text-white/55 mt-2 mb-4 leading-relaxed">
            One-click test. We send a branded email from <span className="font-mono-num">{status?.sender_email || "no-reply@clinq.africa"}</span> via Resend.
            If it lands in the inbox — DNS is good. If it&apos;s in Spam — DNS is propagating.
          </p>
          <div className="flex items-center gap-2">
            <input type="email" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="your@inbox.com" data-testid="test-email-to"
              className="flex-1 px-3 py-2 rounded-md text-sm focus:outline-none focus:border-[#0A8B85]"
              style={{ backgroundColor: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} />
            <button onClick={send} disabled={sending || !to} data-testid="send-test-email"
              className="px-4 py-2 rounded-md font-semibold text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              <PaperPlaneTilt size={12} weight="bold" /> {sending ? "Sending…" : "Send test"}
            </button>
          </div>

          {lastSend && (
            <div className="mt-4 rounded-md p-3 text-xs" style={{ backgroundColor: "rgba(10, 139, 133, 0.08)", border: "1px solid rgba(10, 139, 133, 0.3)" }}>
              <div className="font-bold text-white/85 inline-flex items-center gap-1.5">
                <CheckCircle size={12} weight="fill" style={{ color: C.teal }} /> Sent to {lastSend.to}
              </div>
              <div className="text-white/55 mt-1 font-mono-num text-[11px]">Resend id: {lastSend.id}</div>
              <div className="text-white/45 mt-2 text-[11px] leading-relaxed">
                Open your inbox (and the Spam folder) to confirm. You can also see the live log at{" "}
                <a href="https://resend.com/emails" target="_blank" rel="noreferrer" className="underline" style={{ color: C.tealSoft }}>resend.com/emails</a>.
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function DNSField({ label, value, long }) {
  const copy = () => navigator.clipboard.writeText(value).then(() => toast.success("Copied"));
  return (
    <div className="flex items-start gap-2 mt-1">
      <span className="text-white/40 uppercase tracking-wider font-bold text-[9px] w-14 shrink-0 pt-0.5">{label}</span>
      <span className={`flex-1 font-mono-num text-white/85 ${long ? "break-all" : "truncate"}`}>{value}</span>
      <button onClick={copy} className="text-white/30 hover:text-white" title="Copy">
        <Copy size={11} />
      </button>
    </div>
  );
}

/* Iter109 — Admin view over the shared Supabase `chat_history` table.
   Read-only. Two-pane split: left rail = list of founders who have chatted
   with David (sorted by recency), right pane = full transcript for the
   selected founder across every session in every Clinq app. */
function CrashesPanel() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/client-errors").then(({ data }) => setRows(data.rows || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  return (
    <section data-testid="admin-crashes-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-white/45">Boundary-caught frontend crashes — last 30 days · newest first</div>
        <button onClick={load} data-testid="admin-refresh-crashes"
          className="px-3 py-2 rounded-md text-xs font-semibold" style={{ backgroundColor: C.teal, color: "#fff" }}>Refresh</button>
      </div>
      <div className="rounded-xl border overflow-x-auto" style={{ borderColor: "rgba(123, 211, 206, 0.12)" }}>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-white/45 uppercase tracking-wider text-[10px]" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Page</th>
              <th className="px-3 py-2">Founder</th>
              <th className="px-3 py-2">Error</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="5" className="px-3 py-8 text-center text-white/30">No crashes reported — the ErrorBoundary hasn't caught anything.</td></tr>
            )}
            {rows.map((r) => (
              <React.Fragment key={r.error_id}>
                <tr className="border-t border-white/5" data-testid={`crash-row-${r.error_id}`}>
                  <td className="px-3 py-2 font-mono-num text-white/55 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono-num text-white/80 max-w-[220px] truncate" title={r.path}>{r.path || "—"}</td>
                  <td className="px-3 py-2 text-white/60">{r.email || (r.user_id ? r.user_id.slice(0, 14) : "anonymous")}</td>
                  <td className="px-3 py-2 truncate max-w-md" style={{ color: "#F87171" }} title={r.message}>{r.message || "—"}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => setOpen(open === r.error_id ? null : r.error_id)}
                      data-testid={`crash-stack-toggle-${r.error_id}`}
                      className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.tealSoft }}>
                      {open === r.error_id ? "Hide stack" : "Stack"}
                    </button>
                  </td>
                </tr>
                {open === r.error_id && (
                  <tr className="border-t border-white/5">
                    <td colSpan="5" className="px-3 py-3" style={{ backgroundColor: "rgba(0,0,0,0.35)" }}>
                      <pre className="text-[10px] leading-relaxed whitespace-pre-wrap text-white/55 max-h-64 overflow-y-auto">
                        {(r.stack || "no stack") + "\n\n--- component stack ---\n" + (r.component_stack || "—")}
                      </pre>
                      <div className="text-[10px] text-white/30 mt-1">{r.user_agent}</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DavidChatsPanel() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [convo, setConvo] = useState(null);
  const [loadingConvo, setLoadingConvo] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setLoadingUsers(true);
    api.get("/admin/david/users")
      .then(({ data }) => { setUsers(data.users || []); setError(""); })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Could not load David users.");
        setUsers([]);
      })
      .finally(() => setLoadingUsers(false));
  }, []);

  useEffect(() => {
    if (!selectedEmail) { setConvo(null); return; }
    setLoadingConvo(true);
    api.get(`/admin/david/conversations?email=${encodeURIComponent(selectedEmail)}`)
      .then(({ data }) => setConvo(data))
      .catch(() => setConvo({ messages: [], message_count: 0, session_count: 0 }))
      .finally(() => setLoadingConvo(false));
  }, [selectedEmail]);

  const filtered = users.filter((u) =>
    !search || (u.email || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <section data-testid="admin-david-section" className="grid lg:grid-cols-[320px,1fr] gap-4">
      {/* Left rail — user list */}
      <div className="rounded-xl border overflow-hidden flex flex-col" style={{ borderColor: "rgba(123, 211, 206, 0.12)", maxHeight: "70vh" }}>
        <div className="p-3 border-b" style={{ borderColor: "rgba(255,255,255,0.06)", backgroundColor: "rgba(0,0,0,0.4)" }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email…" data-testid="admin-david-search"
            className="w-full px-2.5 py-1.5 rounded-md text-xs focus:outline-none focus:border-[#0A8B85]"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff" }} />
          <div className="text-[10px] text-white/40 mt-1.5 font-mono-num tracking-wider">
            {loadingUsers ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? "founder" : "founders"}`}
          </div>
        </div>
        <div className="overflow-y-auto flex-1" data-testid="admin-david-users">
          {error && <div className="p-4 text-[11px] text-red-300">{error}</div>}
          {!loadingUsers && filtered.length === 0 && !error && (
            <div className="p-6 text-center text-white/30 text-xs">No David chats yet.</div>
          )}
          {filtered.map((u) => (
            <button key={u.email} onClick={() => setSelectedEmail(u.email)}
              data-testid={`admin-david-user-row`}
              className={`w-full text-left px-3 py-2.5 border-b transition-colors ${selectedEmail === u.email ? "bg-white/8" : "hover:bg-white/5"}`}
              style={{ borderColor: "rgba(255,255,255,0.04)" }}>
              <div className="text-xs font-semibold text-white/90 truncate">{u.email}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-white/45 font-mono-num tabular-nums">
                  {u.message_count} msg · {timeAgo(u.last_message_at)}
                </span>
                {u.other_app_count > 0 && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: "rgba(245,158,11,0.12)", color: "#F59E0B" }}
                    title={`${u.other_app_count} messages from the other Clinq app`}>
                    Cross-app
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right pane — transcript */}
      <div className="rounded-xl border flex flex-col overflow-hidden" style={{ borderColor: "rgba(123, 211, 206, 0.12)", maxHeight: "70vh" }}>
        {!selectedEmail ? (
          <div className="flex-1 grid place-items-center text-white/30 text-sm" data-testid="admin-david-empty">
            Pick a founder from the left to see their full David transcript.
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)", backgroundColor: "rgba(0,0,0,0.4)" }}>
              <div>
                <div className="text-sm font-bold text-white" data-testid="admin-david-selected-email">{selectedEmail}</div>
                {convo && (
                  <div className="text-[10px] text-white/45 font-mono-num tracking-wider mt-0.5">
                    {convo.message_count} messages · {convo.session_count} sessions
                  </div>
                )}
              </div>
              <button onClick={() => setSelectedEmail(null)} data-testid="admin-david-clear-selection"
                className="text-white/40 hover:text-white p-1"><XCircle size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-david-transcript">
              {loadingConvo && <div className="text-white/40 text-xs text-center py-8">Loading transcript…</div>}
              {!loadingConvo && convo && convo.messages.length === 0 && (
                <div className="text-white/30 text-xs text-center py-8">No messages for this founder.</div>
              )}
              {!loadingConvo && convo && convo.messages.map((m) => (
                <TranscriptBubble key={m.id} m={m} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function TranscriptBubble({ m }) {
  const isUser = m.role === "user";
  const isCrossApp = !(m.session_id || "").startsWith("clinq_web_");
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`admin-david-msg-${m.role}`}>
      <div className="max-w-[82%] rounded-lg px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap"
        style={isUser ? {
          backgroundColor: "rgba(10, 139, 133, 0.6)", color: "#fff", borderBottomRightRadius: "3px",
        } : {
          backgroundColor: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(123, 211, 206, 0.12)",
          color: "rgba(255,255,255,0.92)",
          borderBottomLeftRadius: "3px",
        }}>
        <div className="flex items-center gap-1.5 mb-1 text-[9px] font-bold uppercase tracking-wider opacity-70">
          <span>{isUser ? "Founder" : "David"}</span>
          <span className="opacity-60">·</span>
          <span className="font-mono-num tabular-nums">{timeAgo(m.created_at)}</span>
          {isCrossApp && (
            <span className="px-1.5 py-0.5 rounded"
              style={{ backgroundColor: "rgba(245,158,11,0.20)", color: "#F59E0B" }}
              title={`session_id: ${m.session_id || "(none)"} — written by another Clinq app`}>
              Other app
            </span>
          )}
        </div>
        {m.content}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "—";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
